import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { X, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";

export default function CartSheet() {
  const { cart, open, setOpen, removeItem, updateQty } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shabbatHold, setShabbatHold] = useState(false);

  const goCheckout = async () => {
    if (!user) {
      setOpen(false);
      navigate("/login?redirect=/cart");
      return;
    }
    try {
      const { data } = await client.post("/payments/checkout", { origin_url: window.location.origin, shabbat_hold: shabbatHold });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#F7F5F0] border-l border-[#EAE5D9] w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="border-b border-[#EAE5D9] p-6">
          <SheetTitle className="font-serif text-3xl tracking-tighter text-left">Your Bag</SheetTitle>
          <p className="label-uppercase text-[#4A4A4A] text-left">{cart.items.length} item(s)</p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="cart-items-list">
          {cart.items.length === 0 && (
            <div className="text-center py-16">
              <p className="font-serif text-2xl mb-4">Your bag is empty.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="btn-outline-ink">Browse the Shop</Link>
            </div>
          )}
          {cart.items.map((item, idx) => {
            const p = item.product;
            if (!p) return null;
            return (
              <div key={idx} className="flex gap-4" data-testid={`cart-item-${idx}`}>
                <div className="w-24 h-32 bg-[#EAE5D9] shrink-0 overflow-hidden">
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between gap-3">
                    <div>
                      <p className="font-serif text-lg leading-tight">{p.name}</p>
                      <p className="text-xs text-[#4A4A4A] mt-1">
                        {item.size && `Size ${item.size}`}{item.color && ` · ${item.color}`}
                      </p>
                    </div>
                    <button onClick={() => removeItem(idx)} data-testid={`cart-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center border border-[#111111]">
                      <button onClick={() => updateQty(idx, item.quantity - 1)} className="p-2" data-testid={`cart-decrement-${idx}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 text-sm">{item.quantity}</span>
                      <button onClick={() => updateQty(idx, item.quantity + 1)} className="p-2" data-testid={`cart-increment-${idx}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="font-serif text-lg">${(p.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[#EAE5D9] p-6 space-y-4">
          <div className="flex justify-between font-serif text-2xl">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">${cart.total?.toFixed(2) ?? "0.00"}</span>
          </div>
          <label className="flex gap-3 items-start cursor-pointer text-sm" data-testid="cart-shabbat-hold">
            <input type="checkbox" checked={shabbatHold} onChange={e => setShabbatHold(e.target.checked)}
                   className="mt-1 w-4 h-4 accent-[#B24C38]"/>
            <span>
              <span className="label-uppercase text-[#B24C38] block">Shabbat delivery hold</span>
              <span className="text-xs text-[#4A4A4A] block mt-0.5">Pause shipping over Shabbos & Yom Tov.</span>
            </span>
          </label>
          <p className="text-xs text-[#4A4A4A]">Shipping calculated at checkout.</p>
          <button data-testid="cart-checkout-btn" onClick={goCheckout} disabled={cart.items.length === 0}
            className="btn-terracotta w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            Checkout →
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
